import { useEffect, useRef, useState } from "react";
import { TouchableOpacity, View, Text, StyleSheet, Alert } from "react-native";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RankingType } from "./RankingComponents/types";
import { useDevice } from "../../../utils/hooks/useDevice";
import SvaraModal from "./SvaraModal";
import { BtnCloseModal } from "../../../assets";
import {
  selectMyTablesError,
  selectMyTablesItems,
  selectMyTablesLoading,
} from "../../../store/lobby/lobbySagas";
import {
  clearCreateTableError,
  createSpecialTableRequest,
  myTablesRequest,
} from "../../../store/lobby/lobbyActions";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import { RootState } from "../../../store";
import ErrorVipModal from "./ErrorCreateVipTableModal";

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface IProps {
  onClose: () => void;
}

const TURN_TIMES = [10, 15, 20, 30, 60];

const RowWithArrows = ({
  label,
  subtitle,
  onPrev,
  onNext,
}: {
  label: string;
  subtitle: string;
  onPrev: () => void;
  onNext: () => void;
}) => (
  <View style={styles.row}>
    <TouchableOpacity style={styles.arrowBtn} onPress={onPrev}>
      <Text style={styles.arrowText}>{"<"}</Text>
    </TouchableOpacity>
    <View style={styles.rowTexts}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowSubtitle}>{subtitle}</Text>
    </View>
    <TouchableOpacity style={styles.arrowBtn} onPress={onNext}>
      <Text style={styles.arrowText}>{">"}</Text>
    </TouchableOpacity>
  </View>
);

const Divider = () => <View style={styles.divider} />;

const Radio = ({ selected }: { selected: boolean }) => (
  <View style={[styles.radioOuter, selected && styles.radioOuterSelected]}>
    {selected && <View style={styles.radioInner} />}
  </View>
);

const STAKES_CONFIG = [
  { name: "beginner", stake: 5 },
  { name: "rookie", stake: 20 },
  { name: "advanced", stake: 100 },
  { name: "semi", stake: 200 },
  { name: "profesional", stake: 500 },
  { name: "talanted", stake: 1000 },
];

const ALL_STRUCTURES = [
  // Beginner (5)
  { label: "short", min: 50, max: 250, forStake: 5 },
  { label: "normal", min: 100, max: 500, forStake: 5 },
  { label: "high", min: 250, max: 750, forStake: 5 },
  { label: "deep", min: 500, max: 1000, forStake: 5 },
  { label: "unlimited", min: 500, max: 50000000, forStake: 5 },

  // Rookie (20)
  { label: "short", min: 200, max: 1000, forStake: 20 },
  { label: "normal", min: 400, max: 2000, forStake: 20 },
  { label: "high", min: 1000, max: 3000, forStake: 20 },
  { label: "deep", min: 2000, max: 4000, forStake: 20 },
  { label: "unlimited", min: 2000, max: 200000000, forStake: 20 },

  // Advanced (100)
  { label: "short", min: 1000, max: 5000, forStake: 100 },
  { label: "normal", min: 2000, max: 10000, forStake: 100 },
  { label: "high", min: 5000, max: 15000, forStake: 100 },
  { label: "deep", min: 10000, max: 20000, forStake: 100 },
  { label: "unlimited", min: 10000, max: 1000000000, forStake: 100 },

  // Semi (200)
  { label: "short", min: 2000, max: 10000, forStake: 200 },
  { label: "normal", min: 4000, max: 20000, forStake: 200 },
  { label: "high", min: 10000, max: 30000, forStake: 200 },

  // Professional (500)
  { label: "short", min: 5000, max: 25000, forStake: 500 },
  { label: "normal", min: 10000, max: 50000, forStake: 500 },

  // Talented (1000)
  { label: "normal", min: 10000, max: 50000, forStake: 1000 },
];

function VipTablesModal({ onClose }: IProps) {
  const dispatch = useDispatch();
  const items = useSelector(selectMyTablesItems);
  const loading = useSelector(selectMyTablesLoading);
  const error = useSelector(selectMyTablesError);
  const { isDesktop } = useDevice();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  const createdTableId = useSelector(
    (state: RootState) => state.lobby.createdTableId,
  );
  const createdTableLegacyRoomId = useSelector(
    (state: RootState) => state.lobby.createdTableLegacyRoomId,
  );
  const isCreating = useSelector(
    (state: RootState) => state.lobby.createTableLoading,
  );

  const createTableError = useSelector(
    (state: RootState) => state.lobby.createTableError,
  );

  useEffect(() => {
    dispatch(myTablesRequest());
  }, [dispatch]);

  const { isMobile, height, width, isXs } = useDevice();
  const [rankingType, setRankingType] = useState(RankingType.Total);

  const { navigate } = useNavigation<Nav>();
  const { t } = useTranslation();

  const TAB_BAR_H = 40;
  const listH =
    height * 0.75 - (rankingType === RankingType.ChipsTotal ? TAB_BAR_H : 0);

  const [stakeIndex, setStakeIndex] = useState(0);
  const [structureIndex, setStructureIndex] = useState(0);
  const [seats, setSeats] = useState(8);
  const [turnTime, setTurnTime] = useState<number>(30);
  const [isLocked, setIsLocked] = useState<boolean>(true);

  const availableStructures = ALL_STRUCTURES.filter(
    (s) => s.forStake === STAKES_CONFIG[stakeIndex].stake,
  );

  useEffect(() => {
    setStructureIndex(0);
  }, [stakeIndex]);

  useEffect(() => {
    if (createTableError) {
      console.log("is in useEffect createTableErrorrrr", createTableError);

      const errorMessage = createTableError.startsWith("T_SITE_")
        ? t(createTableError)
        : createTableError;

      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
    }
  }, [createTableError, t]);

  useEffect(() => {
    if (createdTableId) {
      console.log("Table created successfully, navigating to:", createdTableId);
      onClose();
      navigate("Game", {
        tableId: String(createdTableId),
        legacyRoomId: createdTableLegacyRoomId,
        roomSlug: "vip",
        isTournamentRoom: false,
      });
    }
  }, [createdTableId, navigate, onClose]);

  const changeSeats = (delta: number) => {
    setSeats((s) => Math.max(2, Math.min(8, s + delta)));
  };

  const handleCreate = () => {
    const stakeConfig = STAKES_CONFIG[stakeIndex];
    const structureConfig = availableStructures[structureIndex];

    const payload = {
      stake: stakeConfig.stake,
      minimalAmount: structureConfig.min,
      maximalAmount: structureConfig.max,
      maxSeats: Math.min(seats, 8),
      turnDuration: turnTime,
      isPrivate: isLocked,
    };

    dispatch(createSpecialTableRequest(payload));
  };

  const getStakeLabel = (index: number) => {
    const stake = STAKES_CONFIG[index];
    const translationKey = `T_SITE_MODAL_NEW_ROOM_${stake.name.toUpperCase()}`;
    const translatedName = t(translationKey);
    return `${translatedName} (${stake.stake})`;
  };

  const getStructureLabel = (index: number) => {
    const structure = availableStructures[index];
    const translationKey = `T_SITE_MODAL_NEW_ROOM_${structure.label.toUpperCase()}`;
    const translatedName = t(translationKey);
    return `${translatedName} (${structure.min} - ${structure.max})`;
  };

  const handlePrevStake = () => {
    setStakeIndex((i) => (i - 1 + STAKES_CONFIG.length) % STAKES_CONFIG.length);
  };

  const handleNextStake = () => {
    setStakeIndex((i) => (i + 1) % STAKES_CONFIG.length);
  };

  const handlePrevStructure = () => {
    setStructureIndex(
      (i) => (i - 1 + availableStructures.length) % availableStructures.length,
    );
  };

  const handleNextStructure = () => {
    setStructureIndex((i) => (i + 1) % availableStructures.length);
  };

  return (
    <>
      <SvaraModal
        visible
        onClose={onClose}
        closeIcon={BtnCloseModal}
        closeButtonSize="big"
        closeButtonStyles={{
          width: 42,
          height: 42,
          ...(isXs && { transform: [{ translateX: 11 }, { translateY: -10 }] }),
        }}
      >
        <View style={[styles.backdrop, { width: isDesktop ? 400 : 300 }]}>
          <View style={styles.card}>
            {/* Header */}
            <View style={styles.headerRow}>
              <Text style={styles.headerText}>
                {t("T_SITE_MODAL_NEW_ROOM_TITLE")}
              </Text>
              {/* <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
                <Text style={styles.closeText}>✕</Text>
              </TouchableOpacity> */}
            </View>

            {/* Миза (Stake) */}
            <RowWithArrows
              label={t("T_SITE_MODAL_NEW_ROOM_STAKE")}
              subtitle={getStakeLabel(stakeIndex)}
              onPrev={handlePrevStake}
              onNext={handleNextStake}
            />

            {/* Чип структура (Chip Structure) */}
            {availableStructures.length > 0 && (
              <RowWithArrows
                label={t("T_SITE_MODAL_NEW_ROOM_CHIP_STRUCTURE")}
                subtitle={getStructureLabel(structureIndex)}
                onPrev={handlePrevStructure}
                onNext={handleNextStructure}
              />
            )}

            {/* Брой места (Seats) */}
            <RowWithArrows
              label={t("T_SITE_MODAL_NEW_ROOM_SEATS")}
              subtitle={String(seats)}
              onPrev={() => changeSeats(-1)}
              onNext={() => changeSeats(1)}
            />

            <Divider />

            {/* Време за ход (Turn Duration) */}
            <Text style={styles.sectionLabel}>
              {t("T_SITE_MODAL_NEW_ROOM_TUNR_DURATION")}
            </Text>
            <View style={styles.turnTimeRow}>
              {TURN_TIMES.map((time) => (
                <TouchableOpacity
                  key={time}
                  onPress={() => setTurnTime(time)}
                  style={styles.turnTimeItem}
                >
                  <Text style={styles.turnTimeText}>
                    {t(`T_SITE_MODAL_NEW_ROOM_${time}_SEC`)}
                  </Text>
                  <View style={{ marginTop: 4 }}>
                    <Radio selected={turnTime === time} />
                  </View>
                </TouchableOpacity>
              ))}
            </View>

            <Divider />

            {/* Заключена (Locked) */}
            <View style={styles.lockRow}>
              <Text style={styles.sectionLabel}>
                {t("T_SITE_MODAL_NEW_ROOM_LOCKED")}
              </Text>
              <View style={styles.lockOptions}>
                <TouchableOpacity
                  style={styles.lockOption}
                  onPress={() => setIsLocked(true)}
                >
                  <Text style={styles.optionText}>
                    {t("T_SITE_MODAL_NEW_ROOM_YES")}
                  </Text>
                  <View style={{ marginLeft: 6 }}>
                    <Radio selected={isLocked} />
                  </View>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.lockOption}
                  onPress={() => setIsLocked(false)}
                >
                  <Text style={styles.optionText}>
                    {t("T_SITE_MODAL_NEW_ROOM_NO")}
                  </Text>
                  <View style={{ marginLeft: 6 }}>
                    <Radio selected={!isLocked} />
                  </View>
                </TouchableOpacity>
              </View>
            </View>

            {/* Bottom buttons */}
            <View style={styles.bottomRow}>
              <TouchableOpacity
                style={[styles.btn, styles.cancelBtn]}
                onPress={onClose}
                disabled={isCreating}
              >
                <Text style={styles.btnText}>
                  {t("T_SITE_MODAL_NEW_ROOM_CANCEL")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btn, styles.createBtn]}
                onPress={handleCreate}
                disabled={isCreating}
              >
                <Text style={styles.btnText}>
                  {t("T_SITE_MODAL_NEW_ROOM_SUBMIT")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </SvaraModal>

      {/* Error Modal */}
      <ErrorVipModal
        visible={showErrorModal}
        title={createTableError ? t(createTableError) : "Error"}
        message={errorModalMessage}
        onClose={() => {
          setShowErrorModal(false);
          dispatch(clearCreateTableError());
        }}
        buttonText={t("T_OK") || "OK"}
      />
    </>
  );
}

export default VipTablesModal;

const styles = StyleSheet.create({
  backdrop: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 20,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  headerText: {
    flex: 1,
    textAlign: "center",
    color: "#fff",
    fontSize: 20,
    fontWeight: "700",
  },
  closeBtn: {
    position: "absolute",
    right: 0,
    padding: 6,
  },
  closeText: {
    color: "#fff",
    fontSize: 20,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
  },
  rowTexts: {
    flex: 1,
    alignItems: "center",
  },
  rowLabel: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
  },
  rowSubtitle: {
    color: "#ccc",
    fontSize: 14,
    marginTop: 2,
  },
  arrowBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#4a353d",
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: "#5a3b44",
    marginVertical: 8,
  },
  sectionLabel: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    marginTop: 4,
  },
  turnTimeRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
    paddingHorizontal: 4,
  },
  turnTimeItem: {
    alignItems: "center",
    marginBottom: 4,
  },
  turnTimeText: {
    color: "#fff",
    fontSize: 12,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: "#aaa",
    justifyContent: "center",
    alignItems: "center",
  },
  radioOuterSelected: {
    borderColor: "#ffcc66",
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#ffcc66",
  },
  lockRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    justifyContent: "space-between",
  },
  lockOptions: {
    flexDirection: "row",
  },
  lockOption: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 20,
  },
  optionText: {
    color: "#fff",
    fontSize: 14,
  },
  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  btn: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
    marginHorizontal: 4,
  },
  cancelBtn: {
    backgroundColor: "#e53935",
  },
  createBtn: {
    backgroundColor: "#43a047",
  },
  btnText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
  },
});
